// File: src/layouts/TopMenuBar.js
import React, { forwardRef, useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { useProfile } from "../context/ProfileContext";
import NotificationsModal from '../components/notifications/NotificationsModal';
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useSharedAnimation } from "../context/AnimationContext";
import { useSearch } from "../context/SearchContext";
import PointsBreakdownModal from "../components/fans/PointsBreakdownModal";

// Import icons
import homeIcon from "../assets/home.png";
import homeDarkIcon from "../assets/home_darkmode.png";
import homeActiveIcon from "../assets/home_active.png";
import pressIcon from "../assets/press.png";
import pressDarkIcon from "../assets/press_darkmode.png";
import pressActiveIcon from "../assets/press_active.png";
import unreadPressIcon from "../assets/unreadNotifications.png";
import unreadPressDarkIcon from "../assets/unreadNotifications_darkmode.png";
import fanIcon from "../assets/fan.png";
import fanDarkIcon from "../assets/fan_darkmode.png";
import fanActiveIcon from "../assets/fan_active.png";
import settingsIcon from "../assets/settings.png";
import settingsDarkIcon from "../assets/settings_darkmode.png";
import settingsActiveIcon from "../assets/settings_active.png";
import dashboardIcon from "../assets/dashboard.png";
import dashboardDarkIcon from "../assets/dashboard_darkmode.png";
import dashboardActiveIcon from "../assets/dashboard_active.png";

import tipIcon from "../assets/tip_icon.png";
import tipWhiteIcon from "../assets/tip_white.png";

import FanbuzzLightIcon from '../assets/fanbuzz_icon.png';
import FanbuzzLightUnreadIcon from '../assets/fanbuzz_iconUnread.png';
import FanbuzzDarkIcon from '../assets/fanbuzz_darkmode.png';
import FanbuzzDarkUnreadIcon from '../assets/fanbuzz_darkmodeUnread.png';

const TopMenuBar = forwardRef(({ userCredits = 0, isTargetHitting, animateCreditTopUp = false, onOpenNotifications, unreadCount = 0 }, ref) => {
  const { searchTerm, setSearchTerm, isSearchVisible, setIsSearchVisible } = useSearch();

  // Consume userProfile directly from ProfileContext
  const { userProfile, activeProfile, unreadPostCount, feedReturnPath, isAdmin, isDarkMode } = useProfile();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unreadArtistNotificationCount, setUnreadArtistNotificationCount] = useState(0);
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);

  const [pointsBalance, setPointsBalance] = useState(0);
  const [showFloatingPoints, setShowFloatingPoints] = useState(false);
  const [floatingPointsValue, setFloatingPointsValue] = useState(3);
  const prevPoints = useRef(null);
  const pointsAnimationControls = useAnimation();
  const textAnimationControls = useAnimation();

  const { streakAnimationControls } = useSharedAnimation();
  const location = useLocation();
  const navigate = useNavigate();

  // Route awareness checks
  const isOnHomePage = location.pathname === '/' || location.pathname === '/home';
  const isOnFeedPage = location.pathname.startsWith('/feed');
  const isOnSettingsPage = location.pathname.startsWith('/settings');
  const isOnDashboardPage = location.pathname.startsWith('/dashboard');
  const isOnFansPage = location.pathname.startsWith('/fans');
  const isOnOwnProfilePage = location.pathname === '/profile' || location.pathname === '/profile/';

  const handleOpenSearch = () => {
    setIsSearchVisible(true);
  };

  const handleCloseSearch = () => {
    setIsSearchVisible(false);
    setSearchTerm("");
  };

  const starAnimationControls = useAnimation();
  const prevUnreadCount = useRef(unreadPostCount);
  
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  // Sync Points Balance with ProfileContext & Artist Accounts
  useEffect(() => {
    if (!activeProfile?.id) {
      setPointsBalance(0);
      return;
    }

    if (activeProfile.type === 'user') {
      const currentPoints = userProfile?.points || 0;
      setPointsBalance(currentPoints);
      prevPoints.current = currentPoints;
    } 
    else if (activeProfile.type === 'artist') {
      const db = getFirestore();
      const artistDocRef = doc(db, "artist_accounts", activeProfile.id);

      const unsubscribe = onSnapshot(artistDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const artistPoints = docSnap.data().points || 0;
          setPointsBalance(artistPoints);
          prevPoints.current = artistPoints;
        } else {
          setPointsBalance(0);
        }
      });

      return () => unsubscribe();
    }

    const handleOptimisticPoints = async (event) => {
      const addedPoints = event.detail?.points || 3;
      setFloatingPointsValue(addedPoints);

      setShowFloatingPoints(true);
      setTimeout(() => {
        setShowFloatingPoints(false);
      }, 1000);

      const defaultTextColor = isDarkMode ? "#ffffff" : "#18181b";
      const flashYellowColor = "#FFFF00";

      await pointsAnimationControls.start({
        scale: [1, 1.3, 0.95, 1],
        rotate: [0, -6, 6, 0],
        transition: { duration: 0.35, ease: "easeOut" }
      });

      await textAnimationControls.start({
        color: [
          defaultTextColor, 
          flashYellowColor, 
          defaultTextColor, 
          flashYellowColor, 
          defaultTextColor, 
          flashYellowColor, 
          defaultTextColor
        ],
        transition: { duration: 0.65, ease: "easeInOut" }
      });
    };

    window.addEventListener("optimistic_points_earned", handleOptimisticPoints);

    return () => {
      window.removeEventListener("optimistic_points_earned", handleOptimisticPoints);
    };
  }, [activeProfile?.id, activeProfile?.type, userProfile?.points, pointsAnimationControls, textAnimationControls, isDarkMode]);
  
  // Dynamic Icon Resolvers
  const getHomeIcon = () => {
    if (isOnHomePage) return homeActiveIcon;
    return isDarkMode ? homeDarkIcon : homeIcon;
  };

  const getPressIcon = () => {
    if (isOnFeedPage) return pressActiveIcon;
    if (unreadPostCount > 0) {
      return isDarkMode ? unreadPressDarkIcon : unreadPressIcon;
    }
    return isDarkMode ? pressDarkIcon : pressIcon;
  };

  const getFansIcon = () => {
    if (isOnFansPage) return fanActiveIcon;
    return isDarkMode ? fanDarkIcon : fanIcon;
  };

  const getSettingsIcon = () => {
    if (isOnSettingsPage) return settingsActiveIcon;
    return isDarkMode ? settingsDarkIcon : settingsIcon;
  };

  const getNotificationIcon = () => {
    const hasUnread = unreadCount > 0;

    if (isDarkMode) {
      return hasUnread ? FanbuzzDarkUnreadIcon : FanbuzzDarkIcon;
    } else {
      return hasUnread ? FanbuzzLightUnreadIcon : FanbuzzLightIcon;
    }
  };

  const getFanbuzzNotifIcon = () => {
    const hasUnread = unreadArtistNotificationCount > 0;

    if (isDarkMode) {
      return hasUnread ? FanbuzzDarkUnreadIcon : FanbuzzDarkIcon;
    } else {
      return hasUnread ? FanbuzzLightUnreadIcon : FanbuzzLightIcon;
    }
  };

  const getDashboardIcon = () => {
    if (isOnDashboardPage) return dashboardActiveIcon;
    return isDarkMode ? dashboardDarkIcon : dashboardIcon;
  };

  useEffect(() => {
    if (unreadPostCount > 0 && prevUnreadCount.current === 0) {
      starAnimationControls.start({
        scale: [1, 1.4, 1],
        transition: { duration: 0.3, times: [0, 0.5, 1] }
      });
    }
    prevUnreadCount.current = unreadPostCount;
  }, [unreadPostCount, starAnimationControls]);

  // 1-Read Listener for artist notifications summary
  useEffect(() => {
    if (activeProfile?.type !== 'artist' || !activeProfile?.id) {
      setUnreadArtistNotificationCount(0);
      return;
    }

    const db = getFirestore();
    const summaryDocRef = doc(db, 'artist_accounts', activeProfile.id, 'artist_notifications', 'summary');

    const unsubscribe = onSnapshot(summaryDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const count = docSnap.data().unreadCount || 0;
        setUnreadArtistNotificationCount(count);
      } else {
        setUnreadArtistNotificationCount(0);
      }
    }, (error) => {
      console.error("Error fetching artist notification summary count:", error);
      setUnreadArtistNotificationCount(0);
    });

    return () => unsubscribe();
  }, [activeProfile?.id, activeProfile?.type]);

  return (
    <>
      <div
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          isDarkMode ? "bg-zinc-900 border-b border-zinc-800" : "bg-white border-b border-gray-100"
        }`}
        style={{
          padding: "10px 0",
          boxShadow: isDarkMode ? "none" : "0px 2px 10px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div className="px-4 h-10 w-full flex items-center">
          
          {/* STATE A: FULL SEARCH BAR ACTIVE */}
          {isSearchVisible ? (
            <motion.div 
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full flex items-center gap-x-3"
            >
              <button 
                onClick={handleCloseSearch}
                className={`p-2 rounded-full transition-colors ${
                  isDarkMode ? "hover:bg-zinc-800 text-zinc-400" : "hover:bg-gray-100 text-gray-500"
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
              </button>

              <div className={`flex-grow h-10 rounded-full flex items-center px-4 relative ${
                isDarkMode ? "bg-zinc-800" : "bg-gray-100"
              }`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                  className={`w-5 h-5 mr-3 flex-shrink-0 ${
                    isDarkMode ? "text-zinc-500" : "text-gray-400"
                  }`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>

                <input
                  type="text"
                  placeholder="Search . . ."
                  autoFocus
                  className={`w-full h-full bg-transparent focus:outline-none text-sm ${
                    isDarkMode ? "text-white" : "text-gray-700"
                  }`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 text-zinc-500 hover:text-brand-pink transition-colors"
                  >
                    <span className="text-xl">×</span>
                  </button>
                )}
              </div>
            </motion.div>
          ) : (
            
            /* STATE B: STATIC DISTRIBUTED MENU */
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full flex items-center justify-around"
            >
              
              {/* 1. DYNAMIC HOME ICON */}
              <Link to="/" className="flex items-center flex-shrink-0">
                <img 
                  src={getHomeIcon()} 
                  alt="Home" 
                  className="w-7 h-7 object-contain transition-transform active:scale-95" 
                />
              </Link>
              
              {/* 2. SEARCH TRIGGER */}
              <button 
                onClick={handleOpenSearch}
                className={`p-1 flex-shrink-0 transition-colors ${
                  isDarkMode ? "text-white hover:text-zinc-300" : "text-gray-700 hover:text-gray-900"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
              </button>

              {/* 3. ADMIN LINK */}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  title="Admin Portal" 
                  className={`text-xs font-bold flex-shrink-0 transition-colors ${
                    isDarkMode 
                      ? "text-white hover:text-zinc-300" 
                      : "text-gray-700 hover:text-blue-600"
                  }`}
                >
                  Admin
                </Link>
              )}
              
              {/* 4. NOTIFICATIONS CONTAINER */}
              {activeProfile?.type === 'artist' ? (
                <button 
                  onClick={handleOpenModal} 
                  title={unreadArtistNotificationCount > 0 ? `${unreadArtistNotificationCount} unread notification(s)` : "Artist Notifications"} 
                  className="p-1 flex-shrink-0 transition-transform active:scale-95 focus:outline-none"
                >
                  <motion.img
                    src={getFanbuzzNotifIcon()}
                    alt="Artist Notifications"
                    className="w-6 h-6 object-contain"
                    animate={{ scale: unreadArtistNotificationCount > 0 ? [1, 1.15, 1] : 1 }}
                    transition={{ duration: 0.3 }}
                  />
                </button>
              ) : (
                <div className="flex-shrink-0">
                  {isOnFeedPage ? (
                    <button 
                      onClick={() => navigate(feedReturnPath)} 
                      title="Go Back" 
                      className="p-1 active:scale-90 transition-transform"
                    >
                      <img 
                        src={pressActiveIcon} 
                        alt="Go Back" 
                        className="w-6 h-6 object-contain" 
                      />
                    </button>
                  ) : (
                    <Link to="/feed" title="Press Hub" className="p-1 relative block">
                      <motion.img
                        src={getPressIcon()}
                        alt="Press Hub"
                        className="w-6 h-6 object-contain"
                        animate={starAnimationControls}
                      />
                      {unreadPostCount > 0 && (
                        <span className="absolute top-0 right-0 flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-pink opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-pink"></span>
                        </span>
                      )}
                    </Link>
                  )}
                </div>
              )}

              {/* 4.5. GLOBAL STANDINGS HUB LINK */}
              {activeProfile?.type !== 'artist' && (
                <div className="flex-shrink-0">
                  {isOnFansPage ? (
                    <button
                      onClick={() => navigate(feedReturnPath)}
                      title="Exit Standings"
                      className="p-1"
                    >
                      <img 
                        src={fanActiveIcon} 
                        alt="Exit Standings" 
                        className="w-6 h-6 object-contain" 
                      />
                    </button>
                  ) : (
                    <Link to="/fans" title="Ecosystem Hub" className="p-1 block">
                      <img 
                        src={getFansIcon()} 
                        alt="Ecosystem Hub" 
                        className="w-6 h-6 object-contain" 
                      />
                    </Link>
                  )}
                </div>
              )}

              {/* 5. DASHBOARD ICON */}
              {activeProfile?.type === 'artist' && (
                <div className="flex-shrink-0">
                  {isOnDashboardPage ? (
                    <button 
                      onClick={() => navigate(feedReturnPath)} 
                      title="Close Dashboard" 
                      className="p-1 active:scale-90 transition-transform"
                    >
                      <img 
                        src={dashboardActiveIcon} 
                        alt="Close Dashboard" 
                        className="w-7 h-7 object-contain" 
                      />
                    </button>
                  ) : (
                    <Link to="/dashboard" title="Artist Dashboard" className="p-1 hover:scale-110 transition-transform block">
                      <img 
                        src={getDashboardIcon()} 
                        alt="Artist Dashboard" 
                        className="w-7 h-7 object-contain" 
                      />
                    </Link>
                  )}
                </div>
              )}

              {/* 6. POINTS BALANCE OR SETTINGS ICON */}
              <div className="flex-shrink-0">
                {isOnOwnProfilePage ? (
                  <div className="flex-shrink-0">
                    {isOnSettingsPage ? (
                      <button 
                        onClick={() => navigate(feedReturnPath)} 
                        className="p-1 active:scale-95 transition-transform"
                      >
                        <img 
                          src={settingsActiveIcon} 
                          alt="Close Settings" 
                          className="w-6 h-6 object-contain" 
                        />
                      </button>
                    ) : (
                      <Link 
                        to="/settings" 
                        className="p-1 hover:scale-110 transition-transform block"
                      >
                        <img 
                          src={getSettingsIcon()} 
                          alt="Settings" 
                          className="w-6 h-6 object-contain" 
                        />
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="relative flex items-center justify-center">
                    <AnimatePresence>
                      {showFloatingPoints && (
                        <motion.span
                          initial={{ opacity: 0, y: 0, scale: 0.8 }}
                          animate={{ opacity: 1, y: -22, scale: 1.25 }}
                          exit={{ opacity: 0, y: -34, scale: 1 }}
                          transition={{ duration: 0.7, ease: "easeOut" }}
                          className="absolute left-full ml-1.5 text-xs font-black text-[#FFFF00] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] pointer-events-none z-50 select-none"
                        >
                          +{floatingPointsValue}
                        </motion.span>
                      )}
                    </AnimatePresence>

                    <motion.div
                      ref={ref}
                      animate={pointsAnimationControls}
                      onClick={() => setIsPointsModalOpen(true)}
                      className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black tracking-wide border shadow-sm cursor-pointer ${
                        isDarkMode 
                          ? "bg-zinc-800/90 border-zinc-700/80" 
                          : "bg-zinc-100 border-zinc-200/80 hover:bg-zinc-200/80"
                      }`}
                    >
                      <img 
                        src={isDarkMode ? tipWhiteIcon : tipIcon} 
                        alt="Points" 
                        className="w-3.5 h-3.5 object-contain select-none" 
                      />

                      <motion.span 
                        animate={textAnimationControls}
                        style={{ color: isDarkMode ? "#ffffff" : "#18181b" }}
                        className={`text-xs font-black select-none ${
                          isDarkMode ? "text-white" : "text-zinc-900"
                        }`}
                      >
                        {pointsBalance.toLocaleString()}
                      </motion.span>
                    </motion.div>
                  </div>
                )}
              </div>

            </motion.div>
          )}

        </div>
      </div>

      <NotificationsModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        inboxUnreadCount={unreadArtistNotificationCount}
      />

      <PointsBreakdownModal 
        isOpen={isPointsModalOpen}
        onClose={() => setIsPointsModalOpen(false)}
      />
    </>
  );
});

export default TopMenuBar;